import { DEBIT_CARD_MERCHANT_FEE, PAYMENT_OPTIONS } from 'constants/payment'
import { floatify } from 'lib/maths/mathUtils'
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/featuresSelectors'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import getCreditBalanceForCheckoutCurrency from 'checkout/selectors/payment/getCreditBalanceForCheckoutCurrency'
import getCreditPayAmount from './getCreditPayAmount'
import getDepositAmountBeforeCreditApplied from './deposit/getDepositAmountBeforeCreditApplied'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getPaymentScheduleMerchantFeeEligibleAmount from './paymentSchedule/getPaymentScheduleMerchantFeeEligibleAmount'
import getGiftCardPayAmount from './getGiftCardPayAmount'
import { shouldShowDebitCardMerchantFees } from './paymentType'

const getMerchantFeeAmount = createSelector(
  (state: App.State) => getDepositAmountBeforeCreditApplied(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getGiftCardPayAmount(state),
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => state.system.merchantFeeConfigs,
  (state: App.State, paymentType: App.PaymentOption, merchantFeePaymentType: App.MerchantFeePaymentType | null = null) => merchantFeePaymentType || state.checkout.merchantFeePaymentType,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (_state: App.State, paymentType: App.PaymentOption) => paymentType,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.payment.rebookingID,
  (state: App.State) => state.checkout.payment.useCredit,
  (state: App.State) => getCreditBalanceForCheckoutCurrency(state),
  (state: App.State) => getPaymentScheduleMerchantFeeEligibleAmount(state),
  (state: App.State) => shouldShowDebitCardMerchantFees(state),
  (
    depositAmount,
    creditAmount,
    giftCardAmount,
    payableAfterModifiers,
    merchantFeeConfigs,
    merchantFeePaymentType,
    postPurchase,
    paymentType,
    isSpoofed,
    rebookingID,
    useLECredits,
    creditBalance,
    paymentScheduleMerchantFeeEligibleAmount,
    shouldShowDebitCardMerchantFees,
  ) => {
    // Don't show merchant fees for post purchases
    if (postPurchase) return 0
    if (paymentType === PAYMENT_OPTIONS.RESERVE_FOR_FREE) return 0
    if (!merchantFeePaymentType || merchantFeeConfigs.length === 0) return 0

    let merchantFeePercentage

    // For payments_debit_card_merchant_fees_v3 experiment, if in the debitcard merchant fees variant set the value to 0.3 percent
    if (shouldShowDebitCardMerchantFees && (merchantFeePaymentType === 'mastercardDebit' || merchantFeePaymentType === 'visaDebit')) {
      merchantFeePercentage = DEBIT_CARD_MERCHANT_FEE
    } else {
      merchantFeePercentage = merchantFeeConfigs.find((config) => config.id === merchantFeePaymentType)?.percentage
    }

    if (!merchantFeePercentage) return 0

    let payableStripeTotal = 0
    switch (paymentType) {
      case PAYMENT_OPTIONS.FULL:
        payableStripeTotal = floatify(payableAfterModifiers - creditAmount - giftCardAmount)
        if (payableStripeTotal < 0) {
          payableStripeTotal = 0
        }
        break
      case PAYMENT_OPTIONS.DEPOSIT:
        payableStripeTotal = (isSpoofed && rebookingID && useLECredits) ?
            Math.max(0, floatify(depositAmount - creditBalance)) : floatify(depositAmount)
        break
      case PAYMENT_OPTIONS.PAYMENT_SCHEDULE:
        payableStripeTotal = floatify(paymentScheduleMerchantFeeEligibleAmount)
        break
    }

    const merchantFee = floatify(payableStripeTotal * (merchantFeePercentage / 100))
    return merchantFee
  },

)

export default getMerchantFeeAmount
